import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { formatET, isPastET } from '../lib/timezone';
import { Calendar as CalendarIcon, X, Image, Palette, Eye, Facebook, Smile, Crop, MapPin, Hash, Clock, Sparkles, Send, Plus, Film, GripVertical, StickyNote } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ImageLibrary } from './ImageLibrary';
import { PostPreview } from './PostPreview';
import { ImageCropTool } from './ImageCropTool';
import { postsApi, filesApi, authFetch } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useClient } from '../context/ClientContext';
import { cn } from '../lib/utils';
import EmojiPicker from 'emoji-picker-react';
import axios from 'axios';

const PLATFORMS = [
  { value: 'general', label: 'General' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'twitter', label: 'Twitter / X' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'tiktok', label: 'TikTok' }
];

const STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'in_review', label: 'In Review' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'posted', label: 'Posted' }
];

const POST_TYPES = [
  { value: 'none', label: 'None', hint: '', studioTool: null },
  { value: 'Photo', label: 'Photo', hint: 'Single image post', studioTool: 'edit' },
  { value: 'Video', label: 'Video', hint: 'Reel, clip, or video post', studioTool: null },
  { value: 'Carousel', label: 'Carousel', hint: 'Multi-slide swipeable post', studioTool: 'carousel' },
  { value: 'Story', label: 'Story', hint: 'Vertical 9:16 format', studioTool: 'edit', canvasPreset: 'story' },
  { value: 'Text', label: 'Text', hint: 'Text overlay on image', studioTool: 'text' },
];

// Map post type to studio URL params
function getStudioUrl(postId, postType, returnPath) {
  const base = `/studio?postId=${postId}&returnTo=${encodeURIComponent(returnPath)}`;
  const typeConfig = POST_TYPES.find(t => t.value === postType);
  if (!typeConfig?.studioTool) return base;
  let url = `${base}&tool=${typeConfig.studioTool}`;
  if (typeConfig.canvasPreset) url += `&canvas=${typeConfig.canvasPreset}`;
  return url;
}

// Helper to safely parse date
const safeParseDate = (dateStr) => {
  if (!dateStr) return new Date();
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? new Date() : date;
};

export function PostModal({ isOpen, onClose, post, onSave, defaultDate }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [scheduledDate, setScheduledDate] = useState(() => safeParseDate(defaultDate));
  const [platform, setPlatform] = useState('general');
  const [status, setStatus] = useState('draft');
  const [postType, setPostType] = useState('');
  const [visualNotes, setVisualNotes] = useState('');
  const [agencyNotes, setAgencyNotes] = useState('');
  const [theme, setTheme] = useState('');
  const [location, setLocation] = useState('');
  const [scheduledTime, setScheduledTime] = useState('10:00');
  const [mediaItems, setMediaItems] = useState([]); // [{id, url, previewUrl, isVideo, name}]
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showCropTool, setShowCropTool] = useState(false);
  const [cropItemIndex, setCropItemIndex] = useState(null);
  const [hashtagGroups, setHashtagGroups] = useState([]);
  const [showHashtags, setShowHashtags] = useState(false);
  const [isGeneratingCaption, setIsGeneratingCaption] = useState(false);
  const [publishPlatforms, setPublishPlatforms] = useState(['facebook']);
  const contentRef = useRef(null);
  const mediaInputRef = useRef(null);
  const { toast } = useToast();
  const { activeClient } = useClient();
  const clientHashtags = activeClient?.hashtags || [];

  // Load preview blob URL for a media path
  const loadPreviewUrl = async (mediaUrl) => {
    if (!mediaUrl) return null;
    return await filesApi.get(mediaUrl) || await filesApi.get(`/api/library/images/${mediaUrl}/file`);
  };

  useEffect(() => {
    if (post) {
      setTitle(post.title || '');
      setContent(post.content || '');
      setScheduledDate(safeParseDate(post.scheduled_date));
      setPlatform(post.platform || 'general');
      setStatus(post.workflow_status === 'ready_to_post' ? 'scheduled' : (post.workflow_status || 'draft'));
      setPostType(post.post_type || 'none');
      setVisualNotes(post.visual_notes || '');
      setAgencyNotes(post.agency_notes || '');
      setTheme(post.theme || '');
      setLocation(post.location || '');
      setScheduledTime(post.scheduled_time || '10:00');
      setPublishPlatforms(post.publish_platforms?.length > 0 ? post.publish_platforms : ['facebook']);

      // Load media items from media_urls or fallback to image_url
      const urls = post.media_urls?.length > 0 ? post.media_urls : (post.image_url ? [post.image_url] : []);
      if (urls.length > 0) {
        Promise.all(urls.map(async (url) => {
          const isVideo = /\.(mp4|mov|avi|webm)$/i.test(url) || url.includes('video');
          const previewUrl = await loadPreviewUrl(url);
          return { id: url, url, previewUrl, isVideo, name: url.split('/').pop() };
        })).then(items => setMediaItems(items));
      } else {
        setMediaItems([]);
      }
    } else {
      resetForm();
    }
  }, [post, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load hashtag groups when modal opens
  useEffect(() => {
    if (isOpen) {
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/hashtag-groups`)
        .then(res => setHashtagGroups(res.data.groups || []))
        .catch(() => {});
    }
  }, [isOpen]);

  const resetForm = () => {
    setTitle('');
    setContent('');
    setScheduledDate(safeParseDate(defaultDate));
    setPlatform('general');
    setStatus('in_review');
    setPostType('none');
    setVisualNotes('');
    setAgencyNotes('');
    setTheme('');
    setLocation('');
    setScheduledTime('10:00');
    setMediaItems([]);
    setPublishPlatforms(['facebook']);
  };

  // Handle file upload (supports multiple files)
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    setIsUploading(true);
    try {
      for (const file of Array.from(files)) {
        const isVid = file.type.startsWith('video/') || /\.(mp4|mov|avi|webm)$/i.test(file.name);
        const previewUrl = URL.createObjectURL(file);
        // Upload the file
        const result = await filesApi.upload(file);
        setMediaItems(prev => [...prev, {
          id: result.id || result.url,
          url: result.id || result.url,
          previewUrl,
          isVideo: isVid,
          name: file.name
        }]);
      }
      toast({ title: `${files.length > 1 ? files.length + ' files' : 'File'} uploaded` });
    } catch (error) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const removeMediaItem = (index) => {
    setMediaItems(prev => {
      const item = prev[index];
      if (item?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleAICaption = async (action) => {
    setIsGeneratingCaption(true);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/posts/ai-caption`, {
        title: title.trim(),
        content: content.trim(),
        platform,
        action, // "rewrite" or "generate"
        post_id: post?.id || null,
      });
      if (data.caption) {
        setContent(data.caption);
        toast({ title: action === 'rewrite' ? 'Caption rewritten' : 'Caption generated' });
      }
    } catch (error) {
      toast({ title: 'AI generation failed', description: error.response?.data?.detail || error.message, variant: 'destructive' });
    } finally {
      setIsGeneratingCaption(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast({
        title: "Validation error",
        description: "Please enter a title",
        variant: "destructive"
      });
      return;
    }

    if (post?.publish_status === 'missed' && isPastET(scheduledDate, scheduledTime)) {
      toast({
        title: "Pick a future date or time",
        description: "This post missed its slot. Choose a time that hasn't passed yet so it can go out.",
        variant: "destructive"
      });
      return;
    }

    setIsSaving(true);
    try {
      const mediaUrls = mediaItems.map(m => m.url);
      const postData = {
        title: title.trim(),
        content: content.trim(),
        scheduled_date: scheduledDate.toISOString(),
        scheduled_time: scheduledTime || null,
        platform,
        status,
        workflow_status: status,
        image_url: mediaUrls[0] || null,
        media_urls: mediaUrls,
        post_type: postType === 'none' ? null : postType,
        visual_notes: visualNotes.trim() || null,
        agency_notes: agencyNotes.trim() || null,
        theme: theme.trim() || null,
        location: location.trim() || null,
        publish_platforms: publishPlatforms.length > 0 ? publishPlatforms : null
      };

      let result;
      if (post) {
        result = await postsApi.update(post.id, postData);
      } else {
        result = await postsApi.create(postData);
      }
      if (status === 'scheduled' && result?.workflow_status === 'in_review') {
        toast({ title: "Saved as In Review", description: "It moves to Scheduled automatically once the client approves it" });
      } else {
        toast({ title: post ? "Post updated" : "Post created", description: post ? "Your post has been updated" : "Your post has been created" });
      }

      if (onSave) onSave(result);
      window.dispatchEvent(new Event('inbox-updated'));
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save post",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!post) return;
    
    setIsDeleting(true);
    try {
      await postsApi.delete(post.id);
      toast({ title: "Post deleted", description: "Your post has been deleted" });
      if (onSave) onSave(null, true);
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete post",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSelectLibraryImage = (image) => {
    const libraryPath = image.url || `/api/library/images/${image.id}/file`;
    const thumbUrl = image.thumbnail_url ? `${process.env.REACT_APP_BACKEND_URL}${image.thumbnail_url}` : null;
    const isVideo = image.is_video || /\.(mp4|mov|avi|webm)$/i.test(image.name || '');
    // Avoid duplicates
    setMediaItems(prev => {
      if (prev.some(m => m.id === libraryPath)) return prev;
      return [...prev, {
        id: libraryPath,
        url: libraryPath,
        previewUrl: thumbUrl,
        isVideo,
        name: image.name || 'Library image'
      }];
    });
    // Load actual blob preview in background
    loadPreviewUrl(libraryPath).then(blobUrl => {
      if (blobUrl) {
        setMediaItems(prev => prev.map(m => m.id === libraryPath ? { ...m, previewUrl: blobUrl } : m));
      }
    });
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto" data-testid="post-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#1a3d5c]">
            {post ? 'Edit Post' : 'Create New Post'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Client Feedback Banner */}
          {post?.approval_status && post.approval_status !== 'pending' && (
            <div
              className={`rounded-lg border px-4 py-3 ${
                post.approval_status === 'approved'
                  ? 'bg-emerald-50 border-emerald-200'
                  : post.approval_status === 'approved_with_edits'
                  ? 'bg-amber-50 border-amber-200'
                  : post.approval_status === 'do_not_run'
                  ? 'bg-red-50 border-red-200'
                  : 'bg-gray-50 border-gray-200'
              }`}
              data-testid="post-modal-client-feedback"
            >
              <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${
                post.approval_status === 'approved' ? 'text-emerald-700'
                : post.approval_status === 'approved_with_edits' ? 'text-amber-700'
                : post.approval_status === 'do_not_run' ? 'text-red-700'
                : 'text-gray-600'
              }`}>
                {post.approval_status === 'approved' && 'Client Approved'}
                {post.approval_status === 'approved_with_edits' && 'Client Approved with Edits'}
                {post.approval_status === 'do_not_run' && 'Client: Do Not Run'}
              </p>
              {(() => {
                const note = post.approval_note
                  || (post.client_feedback?.length > 0
                      ? post.client_feedback[post.client_feedback.length - 1]?.comment
                      : null);
                return note ? (
                  <p className={`text-sm leading-relaxed ${
                    post.approval_status === 'approved_with_edits' ? 'text-amber-800'
                    : post.approval_status === 'do_not_run' ? 'text-red-800'
                    : 'text-gray-700'
                  }`} data-testid="post-modal-client-note">
                    "{note}"
                  </p>
                ) : null;
              })()}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#0066CC] mb-2 block">
              Title
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Post title..."
              className="border-[#1a3d5c]/15 focus:border-[#0066CC]"
              data-testid="post-title-input"
            />
          </div>

          {/* Content */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-mono uppercase tracking-wider text-[#2b7a8a]">
                Content
              </label>
              <div className="flex items-center gap-0.5">
                {/* AI Caption Buttons */}
                {content.trim() ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-purple-400 hover:text-purple-600 hover:bg-purple-50"
                    onClick={() => handleAICaption('rewrite')}
                    disabled={isGeneratingCaption}
                    data-testid="ai-rewrite-btn"
                    title="Rewrite with AI"
                  >
                    {isGeneratingCaption ? (
                      <span className="w-4 h-4 border-2 border-purple-300 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-purple-400 hover:text-purple-600 hover:bg-purple-50"
                    onClick={() => handleAICaption('generate')}
                    disabled={isGeneratingCaption || !title.trim()}
                    data-testid="ai-generate-btn"
                    title="Generate caption with AI"
                  >
                    {isGeneratingCaption ? (
                      <span className="w-4 h-4 border-2 border-purple-300 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                  </Button>
                )}
                {/* Hashtag Quick-Insert */}
                <Popover open={showHashtags} onOpenChange={setShowHashtags}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-gray-400 hover:text-gray-600"
                      data-testid="hashtag-insert-btn"
                    >
                      <Hash className="w-4 h-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-3" align="end" side="top">
                    <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Insert Hashtags</p>
                    {hashtagGroups.length === 0 ? (
                      <p className="text-xs text-gray-400">No hashtag groups yet. Add them in Settings.</p>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {hashtagGroups.map(g => (
                          <button
                            key={g.id}
                            className="w-full text-left p-2 rounded-lg hover:bg-gray-50 border border-gray-100 transition-colors"
                            onClick={() => {
                              const tags = g.hashtags.join(' ');
                              setContent(prev => prev + (prev && !prev.endsWith('\n') && !prev.endsWith(' ') ? '\n' : '') + tags);
                              setShowHashtags(false);
                            }}
                            data-testid={`insert-hashtag-group-${g.id}`}
                          >
                            <span className="text-sm font-medium text-gray-700">{g.name}</span>
                            <p className="text-xs text-gray-400 truncate mt-0.5">{g.hashtags.join(' ')}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
                <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-gray-400 hover:text-gray-600"
                    data-testid="emoji-picker-btn"
                  >
                    <Smile className="w-4 h-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 border-0" align="end" side="top">
                  <EmojiPicker
                    onEmojiClick={(emojiData) => {
                      const textarea = contentRef.current;
                      if (textarea) {
                        const start = textarea.selectionStart || content.length;
                        const before = content.slice(0, start);
                        const after = content.slice(start);
                        setContent(before + emojiData.emoji + after);
                      } else {
                        setContent(prev => prev + emojiData.emoji);
                      }
                      setShowEmojiPicker(false);
                    }}
                    width={320}
                    height={380}
                    searchPlaceHolder="Search emoji..."
                    previewConfig={{ showPreview: false }}
                    skinTonesDisabled
                    lazyLoadEmojis
                  />
                </PopoverContent>
              </Popover>
              </div>
            </div>
            <Textarea
              ref={contentRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your post content..."
              rows={4}
              className="border-[#1a3a4a]/15 focus:border-[#c5d93d] resize-none"
              data-testid="post-content-input"
            />
          </div>

          {/* Date Picker */}
          {/* Date & Time Row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[#2b7a8a] mb-2 block">
                Scheduled Date
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal border-[#1a3a4a]/15",
                      !scheduledDate && "text-muted-foreground"
                    )}
                    data-testid="date-picker-trigger"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-[#2b7a8a]" />
                    {scheduledDate && !isNaN(scheduledDate.getTime()) ? format(scheduledDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={scheduledDate}
                    onSelect={(date) => date && setScheduledDate(date)}
                    initialFocus
                    data-testid="date-calendar"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[#2b7a8a] mb-2 block">
                <Clock className="w-3 h-3 inline mr-1 -mt-0.5" />
                Scheduled Time
              </label>
              <div className="flex items-center gap-2">
                <select
                  value={scheduledTime}
                  onChange={e => setScheduledTime(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-[#1a3a4a]/15 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0066CC] focus:ring-offset-1"
                  data-testid="time-select"
                >
                  {Array.from({ length: 48 }, (_, i) => {
                    const h = Math.floor(i / 2);
                    const m = i % 2 === 0 ? '00' : '30';
                    const val = `${String(h).padStart(2, '0')}:${m}`;
                    const ampm = h === 0 ? '12' : h > 12 ? String(h - 12) : String(h);
                    const suffix = h < 12 ? 'AM' : 'PM';
                    const display = h === 0 ? `12:${m} AM` : `${ampm}:${m} ${suffix}`;
                    return <option key={val} value={val}>{display}</option>;
                  })}
                </select>
              </div>
            </div>
          </div>

          {/* Publish Status (read-only indicator) */}
          {post && post.publish_status !== 'published' && (['failed', 'missed'].includes(post.publish_status) || post.publish_warning) && (
            <div
              className={cn(
                "flex items-start gap-2 px-3 py-2 rounded-lg text-sm border",
                ['failed', 'missed'].includes(post.publish_status) ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-800 border-amber-200"
              )}
              data-testid="publish-issue-banner"
            >
              <span className={cn("w-2 h-2 rounded-full mt-1.5 flex-shrink-0", ['failed', 'missed'].includes(post.publish_status) ? "bg-red-500" : "bg-amber-500")} />
              <span>
                <strong>{post.publish_status === 'missed' ? 'Missed' : post.publish_status === 'failed' ? 'Publishing failed' : 'Not publishing yet'}:</strong>{' '}
                {['failed', 'missed'].includes(post.publish_status) ? (post.publish_error || 'Unknown error') : post.publish_warning}
              </span>
            </div>
          )}

          {post && (post.publish_status === 'published' || post.published_at) && (
            <div className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-lg text-sm",
              post.publish_status === 'published' ? "bg-green-50 text-green-700 border border-green-200" :
              post.publish_status === 'failed' ? "bg-red-50 text-red-700 border border-red-200" :
              "bg-yellow-50 text-yellow-700 border border-yellow-200"
            )} data-testid="publish-status-badge">
              {post.publish_status === 'published' ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  Published {post.published_at ? `on ${formatET(post.published_at, 'MMM d, yyyy h:mm a')} ET` : ''}
                </>
              ) : post.publish_status === 'failed' ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  Publishing failed — try again from the Publish Queue
                </>
              ) : post.published_at ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  Published {formatET(post.published_at, 'MMM d, yyyy h:mm a')} ET
                </>
              ) : null}
            </div>
          )}

          {/* Platform, Status & Post Type Row */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[#0066CC] mb-2 block">
                Platform
              </label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger className="border-[#1a3d5c]/15" data-testid="platform-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[#0066CC] mb-2 block">
                Status
              </label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="border-[#1a3d5c]/15" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => {
                    const needsApproval = s.value === 'scheduled' && !['approved', 'approved_with_edits'].includes(post?.approval_status);
                    return (
                      <SelectItem key={s.value} value={s.value} disabled={needsApproval} data-testid={`status-option-${s.value}`}>
                        {needsApproval ? 'Scheduled (after client approval)' : s.label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[#0066CC] mb-2 block">
                Post Type
              </label>
              <Select value={postType} onValueChange={setPostType}>
                <SelectTrigger className="border-[#1a3d5c]/15" data-testid="post-type-select">
                  <SelectValue placeholder="Type..." />
                </SelectTrigger>
                <SelectContent>
                  {POST_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      <div className="flex items-center gap-2">
                        <span>{t.label}</span>
                        {t.hint && <span className="text-[10px] text-gray-400 hidden sm:inline">{t.hint}</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Auto-Publish Platforms — shown when scheduled with a time */}
          {['in_review', 'scheduled'].includes(status) && scheduledTime && (
            <div className="bg-gradient-to-r from-blue-50/80 to-indigo-50/80 rounded-lg p-3 border border-blue-100">
              <label className="text-xs font-mono uppercase tracking-wider text-blue-600 mb-2 flex items-center gap-1.5">
                <Send className="w-3 h-3" />
                Auto-Publish To
                <span className="text-[10px] font-normal normal-case text-blue-400 ml-1">(publishes at scheduled time)</span>
              </label>
              <div className="flex flex-wrap items-center gap-2">
                {[
                  { key: 'facebook', label: 'Facebook', color: '#1877F2' },
                  { key: 'instagram', label: 'Instagram', color: '#E4405F' },
                  { key: 'twitter', label: 'X', color: '#000' },
                  { key: 'linkedin', label: 'LinkedIn', color: '#0A66C2' },
                ].map(p => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => {
                      setPublishPlatforms(prev =>
                        prev.includes(p.key)
                          ? prev.filter(x => x !== p.key)
                          : [...prev, p.key]
                      );
                    }}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all ${
                      publishPlatforms.includes(p.key)
                        ? 'text-white border-transparent shadow-sm'
                        : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300'
                    }`}
                    style={publishPlatforms.includes(p.key) ? { backgroundColor: p.color } : {}}
                    data-testid={`publish-platform-${p.key}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-blue-400 mt-1.5">
                Facebook & Instagram may be linked at the Meta level — selecting both ensures coverage.
              </p>
            </div>
          )}

          {/* Theme */}
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#2b7a8a] mb-2 block">
              Theme / Topic
            </label>
            <Input
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              placeholder="e.g., Behind-the-Scenes, Fan Favorite, Menu Spotlight..."
              className="border-[#1a3d5c]/15 focus:border-[#0066CC]"
              data-testid="post-theme-input"
            />
          </div>

          {/* Location */}
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#2b7a8a] mb-2 block">
              <MapPin className="w-3 h-3 inline mr-1 -mt-0.5" />
              Location
            </label>
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g., 123 Main St, Your City"
              className="border-[#1a3d5c]/15 focus:border-[#0066CC]"
              data-testid="post-location-input"
            />
          </div>

          {/* Visual Notes */}
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#2b7a8a] mb-2 block">
              Visual / Graphic Notes
            </label>
            <Textarea
              value={visualNotes}
              onChange={(e) => setVisualNotes(e.target.value)}
              placeholder="Describe the ideal photo, video, or graphic..."
              rows={2}
              className="border-[#1a3a4a]/15 focus:border-[#c5d93d] resize-none"
              data-testid="post-visual-notes-input"
            />
          </div>

          {/* Agency Notes (Internal) */}
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-amber-600 mb-2 flex items-center gap-1.5">
              <StickyNote className="w-3.5 h-3.5" />
              Agency Notes <span className="text-[10px] font-normal text-gray-400 normal-case tracking-normal">(internal only, not visible to clients)</span>
            </label>
            <Textarea
              value={agencyNotes}
              onChange={(e) => setAgencyNotes(e.target.value)}
              placeholder="Internal notes, reminders, strategy context..."
              rows={2}
              className="border-amber-200/50 focus:border-amber-400 bg-amber-50/30 resize-none"
              data-testid="post-agency-notes-input"
            />
          </div>

          {/* Media Section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-mono uppercase tracking-wider text-[#0066CC]">
                Media {mediaItems.length > 0 && <span className="text-gray-400 font-normal">({mediaItems.length})</span>}
              </label>
              <div className="flex items-center gap-1">
                {post && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPreview(true)}
                    className="text-xs"
                    style={{ color: 'var(--brand-primary)' }}
                    data-testid="preview-post-btn"
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    Preview
                  </Button>
                )}
                {post && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const returnPath = window.location.pathname;
                      navigate(getStudioUrl(post.id, postType, returnPath));
                    }}
                    className="text-xs text-[#8b5cf6] hover:text-[#8b5cf6]/80"
                    data-testid="edit-in-studio-btn"
                  >
                    <Palette className="w-3 h-3 mr-1" />
                    Studio
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowImageLibrary(true)}
                  className="text-xs text-[#0066CC] hover:text-[#0066CC]/80"
                  data-testid="open-image-library-btn"
                >
                  <Image className="w-3 h-3 mr-1" />
                  Library
                </Button>
              </div>
            </div>

            {/* Media strip */}
            {mediaItems.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-2 mb-2" data-testid="media-strip">
                {mediaItems.map((item, index) => (
                  <div key={item.id + index} className="relative flex-shrink-0 group" data-testid={`media-item-${index}`}>
                    {item.isVideo ? (
                      <div className="w-24 h-24 rounded-lg bg-black flex items-center justify-center border border-gray-200 overflow-hidden">
                        {item.previewUrl ? (
                          <video src={item.previewUrl} className="w-full h-full object-cover" muted />
                        ) : (
                          <Film className="w-8 h-8 text-white/60" />
                        )}
                        <div className="absolute bottom-1 left-1 bg-black/60 text-white text-[9px] px-1 rounded">Video</div>
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                        {item.previewUrl ? (
                          <img src={item.previewUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="w-8 h-8 text-gray-300" />
                          </div>
                        )}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removeMediaItem(index)}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                      data-testid={`remove-media-${index}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                    {!item.isVideo && item.previewUrl && (
                      <button
                        type="button"
                        onClick={() => { setCropItemIndex(index); setShowCropTool(true); }}
                        className="absolute bottom-1 right-1 bg-white/90 text-amber-600 rounded px-1 py-0.5 text-[9px] font-medium opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                      >
                        <Crop className="w-3 h-3 inline -mt-0.5" /> Crop
                      </button>
                    )}
                    {index === 0 && mediaItems.length > 1 && (
                      <div className="absolute top-1 left-1 bg-blue-500 text-white text-[8px] px-1 rounded font-medium">Cover</div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Upload drop zone / add more button */}
            <div
              className={cn(
                "border-2 border-dashed rounded-lg transition-colors cursor-pointer",
                mediaItems.length > 0 ? "p-3 border-gray-200 hover:border-[#0066CC]/40" : "p-6 border-gray-200 hover:border-[#0066CC]/40",
                isUploading && "opacity-60 pointer-events-none"
              )}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFileUpload(e.dataTransfer.files); }}
              onClick={() => mediaInputRef.current?.click()}
              data-testid="media-drop-zone"
            >
              {isUploading ? (
                <div className="flex items-center justify-center gap-2">
                  <span className="w-5 h-5 border-2 border-[#0066CC] border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm text-gray-500">Uploading...</span>
                </div>
              ) : mediaItems.length > 0 ? (
                <div className="flex items-center justify-center gap-2 text-gray-400">
                  <Plus className="w-4 h-4" />
                  <span className="text-sm">Add more photos or videos</span>
                </div>
              ) : (
                <div className="text-center">
                  <Image className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Drop photos or videos here, or click to upload</p>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG, GIF, WebP, HEIC, MP4, MOV — multiple files OK</p>
                </div>
              )}
              <input
                ref={mediaInputRef}
                type="file"
                accept="image/*,video/mp4,video/quicktime,video/webm,.heic,.heif,.mov"
                multiple
                onChange={(e) => { handleFileUpload(e.target.files); e.target.value = ''; }}
                className="hidden"
                data-testid="media-file-input"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between">
          {post && (
            <div className="flex items-center gap-2">
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={isDeleting}
                data-testid="delete-post-btn"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={async () => {
                  let text = title || '';
                  if (content) text += (text ? '\n\n' : '') + content;
                  if (theme) text += `\n\n#${theme.replace(/\s+/g, '')}`;
                  if (clientHashtags.length > 0) {
                    text += '\n\n' + clientHashtags.join(' ');
                  }
                  try {
                    await navigator.clipboard.writeText(text);
                    toast({ title: 'Copied!', description: 'Caption ready to paste into Facebook' });
                  } catch {
                    toast({ title: 'Copy failed', variant: 'destructive' });
                  }
                }}
                className="border-[#1877F2]/30 text-[#1877F2] hover:bg-[#1877F2]/5 text-xs"
                data-testid="copy-facebook-modal-btn"
              >
                <Facebook className="w-3 h-3 mr-1" />
                Copy for FB
              </Button>
            </div>
          )}
          <div className="flex gap-2 ml-auto">
            <Button 
              variant="outline" 
              onClick={onClose}
              className="border-[#1a3d5c]/30"
              data-testid="cancel-post-btn"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={isSaving}
              className="btn-primary"
              data-testid="save-post-btn"
            >
              {isSaving ? 'Saving...' : (post ? 'Update' : 'Create')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Image Library Modal */}
    <ImageLibrary
      isOpen={showImageLibrary}
      onClose={() => setShowImageLibrary(false)}
      onSelectImage={handleSelectLibraryImage}
      multiSelect={true}
    />

    {/* Post Preview Modal */}
    <PostPreview
      isOpen={showPreview}
      onClose={() => setShowPreview(false)}
      post={post ? {
        ...post,
        title,
        content,
        scheduled_date: scheduledDate?.toISOString(),
        platform,
        status,
        scheduled_time: scheduledTime,
        post_type: postType === 'none' ? null : postType,
        visual_notes: visualNotes,
        agency_notes: agencyNotes,
        theme,
        location,
        image_url: mediaItems[0]?.url || null,
        media_urls: mediaItems.map(m => m.url),
      } : null}
    />

    {/* Image Crop Tool */}
    <ImageCropTool
      isOpen={showCropTool && cropItemIndex !== null}
      onClose={() => { setShowCropTool(false); setCropItemIndex(null); }}
      imageSrc={cropItemIndex !== null ? mediaItems[cropItemIndex]?.previewUrl : null}
      onCropComplete={async ({ file, previewUrl }) => {
        if (cropItemIndex === null) return;
        // Upload cropped image
        try {
          const formData = new FormData();
          formData.append('file', file);
          const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/files/upload`, {
            method: 'POST',
            body: formData,
          });
          const data = await res.json();
          if (data.id) {
            setMediaItems(prev => prev.map((m, i) => i === cropItemIndex
              ? { ...m, url: data.id, previewUrl, name: file.name || m.name }
              : m
            ));
          }
        } catch (err) {
          console.error('Failed to upload cropped image:', err);
        }
        setCropItemIndex(null);
      }}
    />
    </>
  );
}
